<template>
  <div class="fp-wrap">
    <!-- ── Left panel ── -->
    <div class="fp-left">
      <img
        :src="require('../../assets/login-panel.png')"
        alt="Pet Walkies"
        class="fp-panel-img"
      />
    </div>

    <!-- ── Right panel ── -->
    <div class="fp-right">
      <div class="fp-form">
        <!-- Mobile-only logo -->
        <div class="fp-mobile-logo">
          <img
            :src="require('../../assets/walkies-logo.png')"
            alt="Walkies"
            class="fp-mobile-logo__img"
          />
        </div>

        <!-- Screen 1: Enter email + OTP -->
        <template v-if="current_screen === 1">
          <h1 class="fp-title">Forgot Password</h1>
          <p class="fp-subtitle">Don't worry, we will help you recover your account.</p>

          <div class="fp-field-group">
            <label class="fp-label">Email address</label>
            <v-text-field
              v-model="form.email"
              outlined
              dense
              hide-details="auto"
              placeholder="admin@gmail.com"
              prepend-inner-icon="mdi-email-outline"
              :error-messages="errors.email"
              class="fp-field"
            >
              <template #append>
                <v-btn
                  v-if="!isOtpSend"
                  text
                  small
                  color="#1a3d7c"
                  class="mt-n1"
                  @click="isOtpSendFunction"
                >Send Code</v-btn>
                <v-btn
                  v-else
                  text
                  small
                  disabled
                  class="mt-n1"
                >Re-send ({{ countdown }})</v-btn>
              </template>
            </v-text-field>
          </div>

          <div class="fp-field-group">
            <label class="fp-label">Verification code</label>
            <v-text-field
              v-model="form.otp"
              outlined
              dense
              hide-details="auto"
              placeholder="Enter verification code"
              prepend-inner-icon="mdi-shield-key-outline"
              :error-messages="errors.otp"
              class="fp-field"
            />
          </div>

          <v-btn
            :loading="isLoading"
            :disabled="isLoading"
            color="#1a3d7c"
            block
            large
            depressed
            class="fp-btn"
            @click="checkVerfication"
          >Reset Password</v-btn>

          <div class="fp-back">
            <v-btn text small color="#e53935" to="/login">
              <v-icon left small>mdi-arrow-left</v-icon> Back to Login
            </v-btn>
          </div>
        </template>

        <!-- Screen 2: Set new password -->
        <template v-if="current_screen === 2">
          <h1 class="fp-title">Set New Password</h1>
          <p class="fp-subtitle">Choose a strong password for your account.</p>

          <div class="fp-field-group">
            <label class="fp-label">New Password</label>
            <v-text-field
              v-model="form.password"
              :type="show_password ? 'text' : 'password'"
              :append-icon="show_password ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
              outlined
              dense
              hide-details="auto"
              placeholder="New password"
              prepend-inner-icon="mdi-lock-outline"
              :error-messages="errors.password"
              class="fp-field"
              @click:append="show_password = !show_password"
            />
          </div>

          <div class="fp-field-group">
            <label class="fp-label">Confirm Password</label>
            <v-text-field
              v-model="form.soft_password"
              :type="show_soft_password ? 'text' : 'password'"
              :append-icon="show_soft_password ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
              outlined
              dense
              hide-details="auto"
              placeholder="Confirm new password"
              prepend-inner-icon="mdi-lock-check-outline"
              class="fp-field"
              @click:append="show_soft_password = !show_soft_password"
            />
          </div>

          <p class="fp-hint">Min 8 characters with uppercase, lowercase, digit &amp; special character.</p>

          <v-btn
            :loading="isLoading"
            :disabled="isLoading"
            color="#1a3d7c"
            block
            large
            depressed
            class="fp-btn"
            @click="resetPassword"
          >Submit</v-btn>

          <div class="fp-back">
            <v-btn text small color="#e53935" to="/login">
              <v-icon left small>mdi-arrow-left</v-icon> Back to Login
            </v-btn>
          </div>
        </template>

        <!-- Screen 3: Success -->
        <template v-if="current_screen === 3">
          <div class="fp-success">
            <v-icon size="72" color="#22c55e">mdi-check-circle-outline</v-icon>
            <h1 class="fp-title mt-4">Password Changed!</h1>
            <p class="fp-subtitle">Your password has been updated successfully.</p>
            <v-btn
              color="#1a3d7c"
              block
              large
              depressed
              class="fp-btn"
              to="/login"
            >Back to Login</v-btn>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ForgotPasswordPage",
  data() {
    return {
      form: { email: "", otp: "", password: "", soft_password: "", portal: true },
      errors: {},
      current_screen: 1,
      countdown: 60,
      isLoading: false,
      isOtpSend: false,
      show_password: false,
      show_soft_password: false,
    };
  },
  mounted() {
    const companyId = localStorage.getItem("company-id");
    if (companyId && companyId.length) {
      this.getCompany(this.encryptIt(companyId));
    }
  },
  methods: {
    async getCompany(companyId) {
      await axios.get(`/get_company/${companyId}`).catch(() => {});
    },
    async isOtpSendFunction() {
      this.isLoading = true;
      try {
        await axios.post("/forgot_password", this.form);
        this.isOtpSend = true;
        this.countdown = 60;
        const timer = setInterval(() => {
          if (this.countdown > 0) {
            this.countdown--;
          } else {
            clearInterval(timer);
            this.isOtpSend = false;
          }
        }, 1000);
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
    async checkVerfication() {
      this.isLoading = true;
      try {
        const response = await axios.post("/forgot_password", this.form);
        if (response.status === 200) this.current_screen = 2;
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
    async resetPassword() {
      this.isLoading = true;
      try {
        const response = await axios.post("/forgot_password", this.form);
        if (response.status === 200) this.current_screen = 3;
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.fp-wrap {
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
}

/* ── Left panel ── */
.fp-left {
  flex: 0 0 50%;
  background: #EDF4FE;
  display: flex;
  align-items: stretch;
  overflow: hidden;
  position: sticky;
  top: 0;
}

.fp-panel-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;
  display: block;
}

/* ── Right panel ── */
.fp-right {
  flex: 0 0 50%;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 56px;
  overflow-y: auto;
}

.fp-form {
  width: 100%;
  max-width: 480px;
}

.fp-title {
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 6px;
  letter-spacing: -0.02em;
}

.fp-subtitle {
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 32px;
}

.fp-field-group {
  margin-bottom: 20px;
}

.fp-label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

::v-deep .fp-field .v-input__slot {
  border-radius: 8px !important;
  background: #ffffff !important;
}

.fp-btn {
  height: 52px !important;
  border-radius: 8px !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  color: #ffffff !important;
  letter-spacing: 0.02em !important;
  text-transform: none !important;
  margin-bottom: 8px;
}

.fp-back {
  margin-top: 8px;
}

.fp-hint {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-bottom: 20px;
  line-height: 1.5;
}

.fp-success {
  text-align: center;
}

/* ── Mobile logo (hidden on desktop) ── */
.fp-mobile-logo {
  display: none;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .fp-wrap {
    flex-direction: column;
    height: auto;
    overflow: auto;
  }

  .fp-left {
    display: none;
  }

  .fp-right {
    flex: 1;
    padding: 60px 24px 40px;
    min-height: 100vh;
    align-items: flex-start;
  }

  .fp-form {
    width: 100%;
  }

  .fp-mobile-logo {
    display: flex;
    justify-content: center;
    margin-bottom: 28px;
  }

  .fp-mobile-logo__img {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    object-fit: contain;
  }
}
</style>
